import html2canvas from 'html2canvas';

// A4 Landscape dimensions in pixels (at 96 DPI)
export const A4_LANDSCAPE_WIDTH = 1123; // 297mm * 96 DPI / 25.4mm
export const A4_LANDSCAPE_HEIGHT = 700; // 210mm * 96 DPI / 25.4mm

// Page margins and dimensions
export const MARGIN = 20;
export const CONTENT_WIDTH = A4_LANDSCAPE_WIDTH - MARGIN * 2;

// Header/Footer dimensions
export const HEADER_HEIGHT = 32;
export const FOOTER_HEIGHT = 32;

// Logo dimensions
export const LOGO_WIDTH = 22;
export const LOGO_HEIGHT = 22;

// Spacing
export const HORIZONTAL_GAP_PX = 16;
export const VERTICAL_GAP_PX = 24;
export const ROW_TOLERANCE = 5;

// Scaling
export const BASE_SCALE = 1.0; // Full scale for normal widgets
export const MAX_HEIGHT_MARGIN = 20;
export const MAX_WIDTH_MARGIN = 10; // Minimal margin for maximum width

// Footer styling
export const FOOTER_BORDER_COLOR = [243, 156, 18] as const;
export const FOOTER_TEXT_COLOR = [243, 156, 18] as const;
export const FOOTER_BORDER_WIDTH = 1.2;
export const PAGE_NUMBER_FONT_SIZE = 16;

export interface WidgetPosition {
    x: number;
    y: number;
    width: number;
    height: number;
    element: HTMLElement; // The react-grid-item wrapper (used for position/dimension calculations)
    captureElement: HTMLElement; // The inner widget content (used for image capture)
    widgetId?: string; // Widget/Component ID extracted from React key
}

// DOM-based positioning that matches UI exactly
export const getWidgetPositionsFromDOM = (container: HTMLElement): WidgetPosition[] => {
    const widgets: WidgetPosition[] = [];
    const widgetElements = container.querySelectorAll('.react-grid-item');

    // Use actual DOM sizes without normalization to preserve original widget sizes
    const containerRect = container.getBoundingClientRect();

    widgetElements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const htmlElement = element as HTMLElement;

        // Extract widget ID from data attribute
        const widgetId = htmlElement.getAttribute('data-report-widget-id') || undefined;

        // The wrapper element (react-grid-item) is used for position calculations
        // The first child is the actual widget content (WidgetInReport) used for capture
        const captureElement = (htmlElement.firstElementChild as HTMLElement) || htmlElement;

        widgets.push({
            x: rect.left - containerRect.left,
            y: rect.top - containerRect.top,
            width: rect.width,
            height: rect.height,
            element: htmlElement,
            captureElement,
            widgetId,
        });
    });

    return widgets.sort((a, b) => a.y - b.y || a.x - b.x);
};

export const groupWidgetsByRows = (widgets: WidgetPosition[]): WidgetPosition[][] => {
    const rows: WidgetPosition[][] = [];

    widgets.forEach((widget) => {
        let addedToRow = false;

        for (let i = 0; i < rows.length; i++) {
            const firstWidgetInRow = rows[i][0];
            const yDiff = Math.abs(widget.y - firstWidgetInRow.y);

            if (yDiff <= ROW_TOLERANCE) {
                rows[i].push(widget);
                addedToRow = true;
                break;
            }
        }

        if (!addedToRow) {
            rows.push([widget]);
        }
    });

    return rows.map((row) => row.sort((a, b) => a.x - b.x));
};

export const captureWidgetAsImage = async (widget: WidgetPosition): Promise<string> => {
    try {
        const canvas = await html2canvas(widget.element, {
            backgroundColor: '#ffffff',
            scale: 5, // Higher scale for better quality
            useCORS: true,
            allowTaint: false,
            logging: false,
            imageTimeout: 0,
            removeContainer: true,
            foreignObjectRendering: false, // Better compatibility
            windowWidth: widget.element.scrollWidth,
            windowHeight: widget.element.scrollHeight,
        });
        return canvas.toDataURL('image/jpeg');
    } catch (error) {
        return '';
    }
};

// Helper to normalize logo to dataURL
export const getLogoDataUrl = async (logo): Promise<string | null> => {
    try {
        if (!logo) return null;
        // If already data URL
        if (typeof logo === 'string' && logo.startsWith('data:')) return logo;
        // If it's a URL, fetch and convert
        if (typeof logo === 'string') {
            const res = await fetch(logo, { mode: 'cors' });
            const blob = await res.blob();
            return await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
            });
        }
        return null;
    } catch (e) {
        return null;
    }
};
// Convert static header logo to data URL
export const getHeaderBrandLogoDataUrl = async (foresightLogo): Promise<string | null> => {
    try {
        if (!foresightLogo) return null;
        // If CRA/webpack gives us a url, fetch and convert
        const res = await fetch(foresightLogo as unknown as string, { mode: 'cors' });
        const blob = await res.blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
        });
    } catch (e) {
        return null;
    }
};

// Create cover page
export const createCoverPage = (pdf, title, reportName, headerBrandLogoDataUrl) => {
    // Cover page title
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(52);
    pdf.setTextColor(44, 62, 80); // Dark blue-gray
    pdf.text(title, 100, 210);

    pdf.setFontSize(46);
    pdf.text(reportName, 100, 255);

    // Data date
    pdf.setFontSize(20);
    pdf.setTextColor(243, 156, 18); // Orange
    const today = new Date();
    const formattedDate = today
        .toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })
        .replace(/ /g, '-');
    pdf.text(`Data Date: ${formattedDate}`, 100, 420);

    // Orange line under date
    pdf.setDrawColor(243, 156, 18);
    pdf.setLineWidth(2.5);
    pdf.line(100, 428, 470, 428);

    // Foresight logo right below the line
    if (headerBrandLogoDataUrl) {
        const logoWidth = 48;
        const logoHeight = 48;
        pdf.addImage(headerBrandLogoDataUrl, 'PNG', 100, 445, logoWidth, logoHeight);
    }
};

export const drawHeaderFooter = (pageNumber: number, logoDataUrl, pdf, headerBrandLogoDataUrl) => {
    if (logoDataUrl) {
        pdf.addImage(logoDataUrl, 'PNG', MARGIN, MARGIN - 1, LOGO_WIDTH, LOGO_HEIGHT);
    }

    const footerTopY = A4_LANDSCAPE_HEIGHT - MARGIN - FOOTER_HEIGHT;
    const footerCenterY = A4_LANDSCAPE_HEIGHT - MARGIN - FOOTER_HEIGHT / 6;

    pdf.setDrawColor(...FOOTER_BORDER_COLOR);
    pdf.setLineWidth(FOOTER_BORDER_WIDTH);
    pdf.line(MARGIN, footerTopY, A4_LANDSCAPE_WIDTH - MARGIN, footerTopY);

    if (headerBrandLogoDataUrl) {
        pdf.addImage(
            headerBrandLogoDataUrl,
            'PNG',
            MARGIN,
            A4_LANDSCAPE_HEIGHT - MARGIN - LOGO_HEIGHT,
            LOGO_WIDTH,
            LOGO_HEIGHT,
        );
    }

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(PAGE_NUMBER_FONT_SIZE);
    pdf.setTextColor(...FOOTER_TEXT_COLOR);
    pdf.text(String(pageNumber), A4_LANDSCAPE_WIDTH - MARGIN, footerCenterY, {
        align: 'right' as any,
    });
};
